"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef } from "react";
import { Phone, Mail, MapPin, Calendar, ClipboardCheck, Video, Stethoscope, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: {
        url: string;
      }) => void;
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement | null;
      }) => void;
    };
  }
}
const CALENDLY_URL = "https://calendly.com/mfloresdo/30min";
const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";
const steps = [{
  icon: Calendar,
  title: "Book Your Free Consultation",
  description: "Choose a time for your free 30-minute consultation with Dr. Flores."
}, {
  icon: ClipboardCheck,
  title: "Complete Your Medical Intake",
  description: "You will receive a brief intake form so we can understand your symptoms, history, and goals."
}, {
  icon: Video,
  title: "Meet With Dr. Flores",
  description: "Review your concerns, discuss appropriate labs, and determine whether Basecamp TRT may be the right next step."
}];
export default function ContactUs() {
  const calendlyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let isMounted = true;
    const loadCalendly = () => {
      if (!isMounted || !window.Calendly || !calendlyRef.current) return;
      calendlyRef.current.innerHTML = "";
      window.Calendly.initInlineWidget({
        url: CALENDLY_URL,
        parentElement: calendlyRef.current
      });
    };
    if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
      const link = document.createElement("link");
      link.href = CALENDLY_CSS;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    const existingScript = document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`) as HTMLScriptElement | null;
    if (existingScript) {
      if (window.Calendly) {
        setTimeout(loadCalendly, 300);
      } else {
        existingScript.addEventListener("load", loadCalendly);
      }
    } else {
      const script = document.createElement("script");
      script.src = CALENDLY_SCRIPT;
      script.type = "text/javascript";
      script.async = true;
      script.onload = loadCalendly;
      document.head.appendChild(script);
    }
    const retryTimer = window.setTimeout(loadCalendly, 1000);
    return () => {
      isMounted = false;
      window.clearTimeout(retryTimer);
    };
  }, []);
  const handleCalendlyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    calendlyRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  return <section id="contact" className="py-10 md:py-16 bg-background overflow-hidden">
      <div className="container w-full max-w-full px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-6xl mx-auto min-w-0">
          <div className="text-center mb-10 md:mb-12 max-w-4xl mx-auto">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-3 py-1.5 sm:px-4 sm:py-2 mb-5">
              <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-primary leading-snug">
                Start With Basecamp TRT
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-tight">
              Ready to Find Out If Low Testosterone Is Part of the Problem?
            </h2>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed max-w-3xl mx-auto">
              Schedule a free 30-minute consultation with Dr. Flores. We serve
              qualified men throughout Texas through secure telemedicine.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start min-w-0">
            <div className="space-y-6 md:space-y-8 min-w-0">
              <div className="bg-card border border-muted-light rounded-xl shadow-soft p-4 sm:p-6 md:p-8 min-w-0 overflow-hidden">
                <h3 className="text-2xl md:text-3xl font-medium text-foreground mb-3 leading-tight">
                  Get in Touch
                </h3>

                <p className="text-sm sm:text-base text-muted-dark leading-relaxed mb-6 md:mb-8">
                  Have questions before booking? Reach out directly or schedule
                  your consultation to discuss symptoms, labs, and whether TRT
                  may be clinically appropriate.
                </p>

                <div className="space-y-5 md:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-secondary-light-bg rounded-md flex items-center justify-center">
                      <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>

                    <div className="min-w-0">
                      <h4 className="font-medium text-foreground mb-1">
                        Call Us
                      </h4>
                      <p className="text-muted-dark break-words">
                        (469) 557-5547
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-accent-light-bg rounded-md flex items-center justify-center">
                      <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>

                    <div className="min-w-0">
                      <h4 className="font-medium text-foreground mb-1">
                        Email Us
                      </h4>
                      <p className="text-muted-dark break-all sm:break-words">
                        Mike@ironpeakmenshealth.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-light-bg rounded-md flex items-center justify-center">
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>

                    <div className="min-w-0">
                      <h4 className="font-medium text-foreground mb-1">
                        Location
                      </h4>
                      <p className="text-muted-dark leading-relaxed break-words">
                        Serving all of Texas via secure telemedicine
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-muted-light">
                  <Button onClick={handleCalendlyClick} className="w-full max-w-full min-h-12 h-auto flex items-center justify-center gap-2 sm:gap-3 bg-accent text-accent-foreground hover:bg-accent-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-accent-active active:translate-y-0 rounded-sm font-semibold transition-all duration-150 px-3 sm:px-6 py-3 text-sm sm:text-base whitespace-normal text-center leading-snug">
                    <SafeHydrate><Calendar className="w-5 h-5 flex-shrink-0" /></SafeHydrate>
                    <span className="block">
                      Book Your Free 30-Minute Consultation
                    </span>
                  </Button>

                  <p className="text-xs md:text-sm text-muted-dark leading-relaxed mt-4 text-center max-w-xl mx-auto">
                    Basecamp TRT starts at $150/month for qualified patients.
                    Treatment is prescribed only when medically appropriate.
                  </p>
                </div>
              </div>

              <div className="bg-primary-light-bg border border-primary/20 rounded-xl p-4 sm:p-6 md:p-8 min-w-0 overflow-hidden">
                <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-5 leading-tight">
                  What Happens Next
                </h3>

                <div className="space-y-5">
                  {steps.map((step, i) => {
                  const IconComponent = step.icon;
                  return <div key={i} data-index={i} className="flex items-start gap-3 sm:gap-4 min-w-0">
                        <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 bg-card rounded-md flex items-center justify-center border border-muted-light">
                          <IconComponent className="w-5 h-5 text-primary" />
                        </div>

                        <div className="min-w-0">
                          <h4 className="font-semibold text-foreground mb-1 leading-snug">
                            Step {i + 1}: {step.title}
                          </h4>

                          <p className="text-sm text-muted-dark leading-relaxed break-words">
                            {step.description}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                <div className="mt-6 pt-5 border-t border-primary/20">
                  <div className="flex items-start gap-3 min-w-0">
                    <Stethoscope className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-dark leading-relaxed break-words">
                      Your consultation is designed to help determine the right
                      next step — not to pressure you into treatment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div id="schedule-consultation" className="bg-card rounded-xl border border-muted-light shadow-soft overflow-hidden w-full max-w-full min-w-0">
              <div className="p-4 sm:p-5 md:p-6 border-b border-muted-light">
                <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2 leading-tight">
                  Schedule Your Consultation
                </h3>

                <p className="text-sm md:text-base text-muted-dark leading-relaxed">
                  Pick a time below for your free 30-minute consultation.
                </p>
              </div>

              <div className="w-full max-w-full overflow-hidden bg-white">
                <div ref={calendlyRef} className="w-full max-w-full" style={{
                minWidth: "0px",
                height: "700px"
              }}>
                  <div className="flex h-full items-center justify-center p-6 text-center">
                    <p className="text-sm text-muted-dark">
                      Loading scheduler...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}